import React, { useMemo } from 'react';
import { Product } from 'types/Product';
import ProductBlock from './ProductBlock';
import { getGatsbySiteUrl } from 'helpers/env';
import { ProductBlockContainer, RelatedProductsContainer, RelatedProductsTitle } from './styled';
import { isBookSizeOption, useProductForm } from 'features/pdp';
import { useProductSelectors } from 'hooks/useProductSelectors';
import { RelatedProductsBySize } from './RelatedProductsBySize/RelatedProductsBySize';
import { PRODUCT_GROUPS_BY_SIZE, useSimilarProductsBySize } from 'hooks/splits/useSimilarProductsBySize';

interface RelatedProductsProps {
  productSku: string;
  products: Product[];
}

/*
 * This component duplicates some of the logic in src/components/RelatedProducts/CartUpsell.tsx.
 * We would prefer to consolidate the two of them, which will involve some discussion with Design.
 */
export function RelatedProducts({ 
  productSku,
  products
}: RelatedProductsProps): React.JSX.Element {
  const { findProductBySku } = useProductSelectors();
  const { form } = useProductForm();
  const showSimilarProductsBySize = useSimilarProductsBySize(productSku);

  const selectedSize = useMemo(() => {
    if (!showSimilarProductsBySize) { return; }

    const formValues = form.getValues();
    const sizeOptionName = Object.keys(formValues).find(isBookSizeOption);
    return formValues[sizeOptionName];
  }, [form, showSimilarProductsBySize]);
  
  const relatedProductsBySize = useMemo(() => {
    if (!showSimilarProductsBySize || !selectedSize) { return; }

    const relatedProductsBySize = PRODUCT_GROUPS_BY_SIZE[selectedSize]
      ?.filter((sku: string) => sku !== productSku)
      ?.map((sku: string) => findProductBySku(sku)?.node)
      ?.filter((product: any) => !!product)
      .slice(0, 5);

    return relatedProductsBySize?.length > 2 && relatedProductsBySize;
  }, [findProductBySku, productSku, selectedSize, showSimilarProductsBySize]);

  if (!products || products.length === 0) {
    return null;
  }

  const relatedProductsTitle = 'You May Also Like';
  
  return relatedProductsBySize ? (
    <RelatedProductsBySize products={relatedProductsBySize} size={selectedSize} />
  ) : (
    <RelatedProductsContainer>
      <RelatedProductsTitle secondary>{relatedProductsTitle}</RelatedProductsTitle>
      <ProductBlockContainer>
        {products.map((product: Product) => {
          return (
            <ProductBlock
              key={product.sku}
              img={product.image}
              badges={[]}
              name={product.name}
              details={`From $${product.lowestPrice || product.price}`}
              href={`${getGatsbySiteUrl()}/${product.canonicalUrl}`}
            />
          );
        })}
      </ProductBlockContainer>
    </RelatedProductsContainer>
  );
}
